import { queryClient } from '@m43/core';
import { stableQueryKeys } from '../common/helpers/stable-query-keys';
import { modals } from '@cad-arc';
import isElectron from 'is-electron';
import { debounce, last, DebouncedFunc } from 'lodash';
import type {
    CadFeatureMap,
    CadNextBootstrapView,
    WorkspaceDto,
    WorkspacePanelTypeEnumType,
    WorkspaceWindowDto,
    WorkspaceWindowTypeEnumType,
} from '@mark43/cad-api';
import { WorkspacePanelTypeEnum } from '@mark43/cad-api';
import { action, computed, makeObservable, observable } from 'mobx';
import { WorkspacePanel } from './workspace-panel';
import type { ClientWorkspacePanel } from './workspace-panel-types';
import { WorkspaceWindow } from '../workspace-windows/workspace-window';
import { useGetOpenPanelLimit } from './use-get-open-panel-limit';
import { useLocation } from 'react-router-dom';
import { getAppName } from '../app-name/app-name';
import { playSound, stopSound } from '~/sounds/play-sound';

type ClientWorkspace = Omit<WorkspaceDto, 'workspaceWindows'> & {
    workspaceWindows: WorkspaceWindow[];
};

class BaseWorkspaceStore {
    @observable
    private data: {
        workspace: ClientWorkspace;
        activeWorkspaceWindowId?: number;
    };

    /**
     * All currently open panels indexed by id or temporaryId.
     */
    @observable
    protected activePanelsById: Record<string, WorkspacePanel> = {};

    private onSaveWorkspaceWindow?: (
        workspacePanelsStore: WorkspaceStore,
        onWorkspaceSave: number
    ) => Promise<WorkspaceWindow | undefined>;

    private broadcastClosePanelsOfType: (workspacePanelType: WorkspacePanelTypeEnumType) => void;

    private getOpenPanelLimit: ReturnType<typeof useGetOpenPanelLimit>;

    private getWorkspaceWindowIdForLocation: (
        location: ReturnType<typeof useLocation>,
        workspace: ClientWorkspace
    ) => number;

    private get hasAnyInactivityAlertingPanels(): boolean {
        return this.activePanels.some((p) => p.isAlerting);
    }

    public syncInactivityAlertSound(): void {
        if (this.hasAnyInactivityAlertingPanels) {
            playSound('draft-event-timer-alert', { mode: 'looping' });
        } else {
            stopSound('draft-event-timer-alert');
        }
    }

    constructor({
        activeWorkspaceWindowId,
        broadcastClosePanelsOfType,
        getOpenPanelLimit,
        workspace,
        onSaveWorkspaceWindow,
        getWorkspaceWindowIdForLocation,
    }: {
        activeWorkspaceWindowId: number | undefined;
        broadcastClosePanelsOfType: (workspacePanelType: WorkspacePanelTypeEnumType) => void;
        getOpenPanelLimit: ReturnType<typeof useGetOpenPanelLimit>;
        workspace: WorkspaceDto;
        onSaveWorkspaceWindow?: (
            workspacePanelsStore: WorkspaceStore,
            workspaceWindowId: number
        ) => Promise<WorkspaceWindow | undefined>;
        getWorkspaceWindowIdForLocation: (
            location: ReturnType<typeof useLocation>,
            workspace: WorkspaceDto
        ) => number;
    }) {
        this.data = {
            workspace: {
                ...workspace,
                workspaceWindows: workspace.workspaceWindows.map((workspaceWindowDto) => {
                    return this.initializeWorkspaceWindow(workspaceWindowDto);
                }),
            },
            activeWorkspaceWindowId,
        };
        this.getWorkspaceWindowIdForLocation = getWorkspaceWindowIdForLocation;
        this.onSaveWorkspaceWindow = onSaveWorkspaceWindow;
        this.broadcastClosePanelsOfType = broadcastClosePanelsOfType;
        this.refreshWorkspace();
        this.getOpenPanelLimit = getOpenPanelLimit;

        makeObservable(this);
    }

    public initializeWorkspaceWindow = (workspaceWindowDto: WorkspaceWindowDto) => {
        return new WorkspaceWindow({
            workspaceWindowDto,
            delegate: this,
        });
    };

    public getWorkspace() {
        return this.data.workspace;
    }

    @computed
    get workspaceName(): string {
        return this.data.workspace.name;
    }

    public getActiveWorkspaceWindowType(): WorkspaceWindowTypeEnumType | undefined {
        return this.data.workspace.workspaceWindows.find(
            (workspaceWindow) => workspaceWindow.id === this.data.activeWorkspaceWindowId
        )?.windowType;
    }

    public getWorkspaceWindowById(windowId: number) {
        return this.data.workspace.workspaceWindows.find(
            (workspaceWindow) => workspaceWindow.id === windowId
        );
    }

    public getWorkspaceWindowsByWindowType(windowType: WorkspaceWindowTypeEnumType) {
        return this.data.workspace.workspaceWindows.filter(
            (workspaceWindow) => workspaceWindow.windowType === windowType
        );
    }

    @computed
    get activeWorkspaceWindowType(): WorkspaceWindowTypeEnumType | undefined {
        return this.data.workspace.workspaceWindows.find(
            (workspaceWindow) => workspaceWindow.id === this.data.activeWorkspaceWindowId
        )?.windowType;
    }

    @action
    setWorkspace(workspace: WorkspaceDto, activeWorkspaceWindowId?: number): void {
        this.data.workspace = {
            ...workspace,
            workspaceWindows: workspace.workspaceWindows.map((workspaceWindowDto) => {
                return this.initializeWorkspaceWindow(workspaceWindowDto);
            }),
        };
        this.data.activeWorkspaceWindowId = activeWorkspaceWindowId;
        this.refreshWorkspace();
    }

    @action
    setWorkspaceWindowIdForLocation(location: ReturnType<typeof useLocation>): void {
        const workspaceWindowId = this.getWorkspaceWindowIdForLocation(
            location,
            this.data.workspace
        );
        if (workspaceWindowId === this.data.activeWorkspaceWindowId) return;
        this.data.activeWorkspaceWindowId = workspaceWindowId;
        this.refreshWorkspace();
    }

    getActiveWorkspaceWindowId(): number | undefined {
        return this.data.activeWorkspaceWindowId;
    }

    getActiveWorkspaceWindow(): WorkspaceWindow {
        const activeWorkspaceWindow = this.data.workspace.workspaceWindows.find(
            (workspaceWindow) => workspaceWindow.id === this.data.activeWorkspaceWindowId
        );
        if (!activeWorkspaceWindow) {
            throw new Error('Unexpectedly did not find active workspace window.');
        }
        return activeWorkspaceWindow;
    }

    @action
    updateWorkspaceWindowWithRemoteValues(updatingWorkspaceWindow: WorkspaceWindowDto): void {
        const workspaceWindowToUpdate = this.data.workspace.workspaceWindows.find(
            (workspaceWindow) => workspaceWindow.id === updatingWorkspaceWindow.id
        );
        if (workspaceWindowToUpdate) {
            Object.assign(workspaceWindowToUpdate, updatingWorkspaceWindow);
            workspaceWindowToUpdate.updateWorkspaceWindow(updatingWorkspaceWindow);
        }
    }

    @action
    private refreshWorkspace(): void {
        for (const key in this.activePanelsById) {
            delete this.activePanelsById[key];
        }
        if (getAppName() === 'cad') {
            const newActiveWorkspaceWindow = this.data.workspace.workspaceWindows.find(
                (workspaceWindow) => workspaceWindow.id === this.data.activeWorkspaceWindowId
            );
            if (newActiveWorkspaceWindow) {
                for (const workspacePanel of newActiveWorkspaceWindow.workspacePanels) {
                    this.addPanel(workspacePanel);
                }
            }
        } else {
            const allWorkspacePanels = this.data.workspace.workspaceWindows
                .map((workspaceWindow) => workspaceWindow.workspacePanels)
                .flat();
            for (const workspacePanel of allWorkspacePanels) {
                this.addPanel(workspacePanel);
            }
        }
        this.syncInactivityAlertSound();
    }

    @computed
    get activePanels(): WorkspacePanel[] {
        return Object.values(this.activePanelsById);
    }

    getActivePanels(): WorkspacePanel[] {
        return Object.values(this.activePanelsById);
    }

    getPersistablePanels(): WorkspacePanel[] {
        return Object.values(this.activePanelsById).filter((workspacePanel) => {
            if (
                (workspacePanel.workspacePanelType === 'EVENT' ||
                    workspacePanel.workspacePanelType === 'BOLO' ||
                    workspacePanel.workspacePanelType === 'MEDIA_ATTACHMENTS') &&
                !workspacePanel.linkedEntityId
            ) {
                return false;
            }
            if (
                workspacePanel.workspacePanelType === 'SCHEDULED_EVENT' &&
                !workspacePanel.linkedEntityId
            ) {
                return false;
            }
            return true;
        });
    }

    /**
     *
     * @param data Creates a new panel and optionally auto-focuses it
     * @param focus
     */
    @action
    public async addPanel(data: ClientWorkspacePanel, focus?: boolean): Promise<void> {
        if (
            WorkspacePanelTypeEnum[data.workspacePanelType].workspacePanelCategory ===
                'SINGLE_INSTANCE' &&
            isElectron() &&
            // the existence of data.id suggests that this panel was part of a workspace that is being
            // loaded, and we don't want to override loading existing workspace state if somehow
            // multiple single instance panels made it in
            !data.id
        ) {
            this.broadcastClosePanelsOfType(data.workspacePanelType);
        }
        const panel = this.createPanel(data);
        if (focus) {
            this.bringPanelToFront(panel);
        } else {
            // If we're not focusing the panel, we slot it in behind the current focused panel
            const sortedPanels = this.activePanels
                .slice()
                .sort((a, b) => ((a.zindex ?? 0) > (b.zindex ?? 0) ? 1 : -1));
            const currentFocusedPanel = last(sortedPanels);
            if (currentFocusedPanel) {
                if (!currentFocusedPanel.zindex) {
                    currentFocusedPanel.zindex = 1;
                }
                panel.zindex = currentFocusedPanel?.zindex;
                currentFocusedPanel.zindex++;
            } else {
                panel.zindex = 1;
            }
            this.recalculateZIndices();
        }
        this.enqueueSaveWorkspaceWindow(data.windowId);
    }

    /**
     * Removes a given panel from the store
     */
    @action
    public removePanel(panel: WorkspacePanel): void {
        delete this.activePanelsById[panel.initialId];
        this.syncInactivityAlertSound();
        this.recalculateZIndices();
        this.enqueueSaveWorkspaceWindow(panel.windowId);
    }

    @action
    public removePanelsOfType(workspacePanelType: WorkspacePanelTypeEnumType) {
        const matchingPanels = this.getActivePanels().filter(
            (panel) => panel.workspacePanelType === workspacePanelType
        );
        matchingPanels.forEach((panel) => this.removePanel(panel));
    }

    private recalculateZIndices(): void {
        const sortedPanels = this.activePanels.slice().sort((a, b) => {
            return (a.zindex ?? 0) > (b.zindex ?? 0) ? 1 : -1;
        });
        for (let i = 0; i < sortedPanels.length; i++) {
            // 1-based z-index
            sortedPanels[i].zindex = i + 1;
        }
    }

    /**
     * Updates a panel's z-index to be the highest on in the current list,
     * thus bringing it in front of all other panels
     */
    @action
    public bringPanelToFront(panel: WorkspacePanel): void {
        if (this.isPanelFocused(panel)) {
            return;
        }
        panel.zindex = Infinity;
        this.recalculateZIndices();
        this.enqueueSaveWorkspaceWindow(panel.windowId);
    }

    public getFocusedPanel(): WorkspacePanel | undefined {
        return this.activePanels.find((workspacePanel) => this.isPanelFocused(workspacePanel));
    }

    public isPanelFocused(panel: WorkspacePanel): boolean {
        return panel.zindex === this.activePanels.length;
    }

    public isPanelTypeFocused(panelType: WorkspacePanelTypeEnumType): boolean {
        const focusedPanel = this.getFocusedPanel();
        return focusedPanel?.workspacePanelType === panelType;
    }

    public isPanelOpenLimitReached(workspacePanelType: WorkspacePanelTypeEnumType): boolean {
        const panelOpenLimit = this.getOpenPanelLimit(workspacePanelType);

        if (panelOpenLimit === null) {
            // no limit defined for this panel type
            return false;
        }

        const panelOpenCount = Object.values(this.activePanelsById).filter(
            (panel) => panel.workspacePanelType === workspacePanelType
        ).length;

        return panelOpenCount >= panelOpenLimit;
    }

    public enqueueSaveWorkspaceWindow: DebouncedFunc<
        (workspaceWindowId: number) => Promise<WorkspaceWindow | undefined>
    > = debounce(
        (workspaceWindowId: number) => {
            return this.onSaveWorkspaceWindow?.(this, workspaceWindowId);
        },
        3000,
        { maxWait: 3000 }
    );

    private createPanel(workspacePanelDto: ClientWorkspacePanel): WorkspacePanel {
        const panel: WorkspacePanel = new WorkspacePanel(workspacePanelDto, this);
        this.activePanelsById[panel.initialId] = panel;
        return panel;
    }
}

/**
 * The only reason that this is separated from BaseWorkspacePanelsStore is so that it can be the
 * place for putting instance specific logic w/o cluttering the core code
 */
export class WorkspaceStore extends BaseWorkspaceStore {
    /**
     * Removes a given panel from the store
     */
    public removePanel(panel: WorkspacePanel, onCloseCallback?: () => void): void {
        if (panel.workspacePanelType === 'EVENT') {
            const enabledFeatures = queryClient.getQueryData<CadFeatureMap>(
                stableQueryKeys.ENABLED_FEATURES_QUERY_KEY
            );
            const bootstrapData = queryClient.getQueryData<CadNextBootstrapView>(
                stableQueryKeys.BOOTSTRAP_QUERY_KEY
            );
            const isNewEvent = !panel.linkedEntityId;

            // first check our enabled features cache
            // if nothing is defined there, check bootstrap
            const confirmBeforeClosingNewEventForms =
                enabledFeatures?.confirmBeforeClosingNewEventForms ??
                bootstrapData?.enabledFeatures.confirmBeforeClosingNewEventForms;

            if (confirmBeforeClosingNewEventForms && isNewEvent) {
                modals.confirm({
                    body:
                        'Are you sure you would like to close? There is a new event with unsaved data.',
                    title: 'Confirm Close',
                    confirmLabel: 'Yes, discard and close',
                    cancelLabel: 'Continue Working',
                    onConfirm: () => {
                        onCloseCallback?.();
                        super.removePanel(panel);
                    },
                });
            } else {
                onCloseCallback?.();
                super.removePanel(panel);
            }
        } else {
            onCloseCallback?.();
            super.removePanel(panel);
        }
    }
}
